import { isReportVisible } from '../utils/reportVisibility';
import Alert from '@amzn/awsui-components-core/alert';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Icon, { IconProps } from '@amzn/awsui-components-core/icon';
import Select from '@amzn/awsui-components-core/select';
import Spinner from '@amzn/awsui-components-core/spinner';
import StatusIndicator, { StatusIndicatorProps } from '@amzn/awsui-components-core/status-indicator';
import React from 'react';
import { useStarGridData } from '../context/StarGridDataContext';
import { HOME_SORT_OPTIONS, useHomeViewState } from '../hooks/useHomeViewState';
import { PnlReportSummary } from '../starGridTypes';
import { computeStatusCounts, formatRowCount, normalizeReportStatus, relativeTime, reportStatusLabel } from '../utils/reportStatus';
import styles from './styles/StarGridHomeView.module.scss';

export interface StarGridHomeViewProps {
  /** Called when the user clicks Create Report — parent routes to the create-report view. */
  onCreate?: () => void;
  /** Called when the user chooses to modify — parent routes to the studio in report mode. */
  onModify?: () => void;
  /** Called when a report card is opened — parent routes to the detail view. */
  onOpenReport?: (reportId: number) => void;
}

/** Card status indicator mapping — pill color + Cloudscape StatusIndicator type + label. */
const STATUS_INDICATOR_TYPE: Record<string, StatusIndicatorProps.Type> = {
  READY: 'success',
  GENERATING: 'in-progress',
  FAILED: 'error',
  DELETED: 'stopped'
};

const STATUS_CLASSNAME: Record<string, string> = {
  READY: styles.statusReady,
  GENERATING: styles.statusGenerating,
  FAILED: styles.statusFailed
};

const ReportCard: React.FC<{ report: PnlReportSummary; onOpen: () => void }> = ({ report, onOpen }) => {
  const status = normalizeReportStatus(report.status);
  return (
    <div
      className={styles.card}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className={styles.cardHeader}>
        <div className={styles.cardTitle} title={report.report_name}>
          {report.report_name}
        </div>
        <div className={`${styles.cardStatus} ${STATUS_CLASSNAME[status] ?? ''}`}>
          <StatusIndicator type={STATUS_INDICATOR_TYPE[status] ?? 'pending'}>{reportStatusLabel(status)}</StatusIndicator>
        </div>
      </div>

      <div className={styles.cardMeta}>
        <span>
          <strong>{formatRowCount(report.row_count)}</strong> rows
        </span>
      </div>

      <div className={styles.dimTags} />

      <div className={styles.cardFooter}>
        <div className={styles.cardAuthor}>
          {report.created_by ?? '—'} · {relativeTime(report.created_at)}
        </div>
        {/* Non-interactive label: the whole card is the button, so a nested interactive control here
            would both fire onOpen twice and nest an interactive element inside role="button". */}
        <div className={styles.cardLink}>View Config →</div>
      </div>
    </div>
  );
};

/** One of the landing page's two choices. Click anywhere on it, not just the button. */
const ActionCard: React.FC<{
  title: string;
  description: string;
  icon: IconProps['name'];
  actionLabel: string;
  footnote?: string;
  disabled?: boolean;
  testId: string;
  onSelect: () => void;
}> = ({ title, description, icon, actionLabel, footnote, disabled = false, testId, onSelect }) => (
  // The primary Button is the interactive control; the card itself is a plain container. Making the
  // card a second role="button" with the same handler double-fired onSelect and nested an interactive
  // element inside another.
  <div className={`${styles.actionCard} ${disabled ? styles.actionCardDisabled : ''}`} aria-disabled={disabled} data-testid={testId}>
    <div className={styles.actionIcon} aria-hidden="true">
      <Icon name={icon} size="big" variant="link" />
    </div>
    <div className={styles.actionTitle}>{title}</div>
    <p className={styles.actionDesc}>{description}</p>
    <div className={styles.actionFooter}>
      <Button variant="primary" disabled={disabled} onClick={onSelect}>
        {actionLabel}
      </Button>
      {footnote ? <span className={styles.actionFootnote}>{footnote}</span> : null}
    </div>
  </div>
);

/** P&L Reports landing view — pick a task, then (for Modify) choose from the saved reports. */
export const StarGridHomeView: React.FC<StarGridHomeViewProps> = ({ onCreate, onModify, onOpenReport }) => {
  const { pnlReports } = useStarGridData();
  const reports = (pnlReports.data ?? []).filter(isReportVisible);
  const { sortKey, setSortKey, visibleReports, mode, setMode } = useHomeViewState(reports);
  const counts = computeStatusCounts(reports);
  const selectedSort = HOME_SORT_OPTIONS.find((o) => o.value === sortKey) ?? HOME_SORT_OPTIONS[0];

  if (mode === 'choose') {
    return (
      <div className={styles.page}>
        <header className={`${styles.header} ${styles.chooseHeader}`}>
          <div>
            <h1 className={styles.title}>P&amp;L Reports</h1>
            <p className={styles.subtitle}>Build a new report from a deck template, or open one you already saved.</p>
          </div>
        </header>
        <div className={styles.actionGrid}>
          <ActionCard
            testId="stargrid-home-card-create"
            icon="add-plus"
            title="Create New Report"
            description="Start from a deck template — pick its rows, scenarios and filters, preview the grid, then save it."
            actionLabel="Create Report"
            onSelect={() => onCreate?.()}
          />
          <ActionCard
            testId="stargrid-home-card-modify"
            icon="edit"
            title="Modify Existing Report"
            description="Open a saved report to change its layout, scenarios or filters, or to review how it is configured."
            actionLabel="Choose Report"
            footnote={counts.total === 1 ? '1 saved report' : `${counts.total} saved reports`}
            disabled={!pnlReports.loading && counts.total === 0}
            onSelect={() => (onModify ? onModify() : setMode('modify'))}
          />
        </div>
        {pnlReports.error && (
          <Alert type="error" header="Could Not Load Reports">
            {pnlReports.error}
          </Alert>
        )}
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <Button variant="inline-link" iconName="angle-left" onClick={() => setMode('choose')} data-testid="stargrid-home-back">
            Back
          </Button>
          <h1 className={styles.title}>Modify Existing Report</h1>
          <p className={styles.subtitle}>Open a report to change its configuration or explore its data.</p>
        </div>
        <div className={styles.headerActions}>
          <Button iconName="refresh" onClick={() => void pnlReports.refresh()} loading={pnlReports.loading} data-testid="stargrid-home-refresh">
            Refresh
          </Button>
          <Button variant="primary" iconName="add-plus" onClick={onCreate} data-testid="stargrid-home-create">
            Create Report
          </Button>
        </div>
      </header>

      <div className={styles.pillRow}>
        <span className={`${styles.pill} ${styles.pillTotal}`}>
          <span className={styles.pillDot} />
          <span className={styles.pillCount}>{counts.total}</span> Total
        </span>
        <span className={`${styles.pill} ${styles.pillReady}`}>
          <span className={styles.pillDot} />
          <span className={styles.pillCount}>{counts.ready}</span> Ready
        </span>
        <span className={`${styles.pill} ${styles.pillGenerating}`}>
          <span className={styles.pillDot} />
          <span className={styles.pillCount}>{counts.generating}</span> Generating
        </span>
        <span className={`${styles.pill} ${styles.pillFailed}`}>
          <span className={styles.pillDot} />
          <span className={styles.pillCount}>{counts.failed}</span> Failed
        </span>
      </div>

      {pnlReports.error && (
        <Alert type="error" header="Could Not Load Reports">
          {pnlReports.error}
        </Alert>
      )}

      <div className={styles.toolbar}>
        <div className={styles.countLabel}>
          Showing <strong>{visibleReports.length}</strong> of <strong>{counts.total}</strong> reports
        </div>
        <div className={styles.sortWrap}>
          <label htmlFor="stargrid-sort-select">Sort By</label>
          <Select
            selectedOption={{ value: selectedSort.value, label: selectedSort.label }}
            options={HOME_SORT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            onChange={({ detail }) => {
              const val = detail.selectedOption.value;
              if (val === 'recent' || val === 'oldest' || val === 'name' || val === 'status') {
                setSortKey(val);
              }
            }}
            data-testid="stargrid-home-sort"
          />
        </div>
      </div>

      {pnlReports.loading && !reports.length ? (
        <Box textAlign="center" padding="l">
          <Spinner /> Loading reports…
        </Box>
      ) : visibleReports.length === 0 ? (
        <div className={styles.empty}>
          <Box variant="p">
            No reports yet. Click <strong>Create Report</strong> to build your first one.
          </Box>
        </div>
      ) : (
        <div className={styles.cardGrid}>
          {visibleReports.map((report) => (
            <ReportCard key={report.pnl_report_id} report={report} onOpen={() => onOpenReport?.(report.pnl_report_id)} />
          ))}
        </div>
      )}
    </div>
  );
};

export default StarGridHomeView;
